/**
 * Quick Mark Lost Modal
 * 
 * Micro-modal with 1 question: "Why?"
 * 2 seconds, done.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceResolver } from '@/hooks/useWorkspaceResolver';
import { useAuth } from '@/lib/auth';
import { Deal } from '@/hooks/useDeals';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { errorMessage } from '@/lib/errors';

const LOSS_REASONS = [
  { value: 'time', label: 'Time / Inactivity' },
  { value: 'price', label: 'Price' },
  { value: 'no_fit', label: 'No Fit' },
  { value: 'ghosted', label: 'Ghosted' },
  { value: 'other', label: 'Other' },
];

interface QuickMarkLostModalProps {
  deal: Deal;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QuickMarkLostModal({ deal, open, onOpenChange }: QuickMarkLostModalProps) {
  const { workspaceId } = useWorkspaceResolver();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [lossReason, setLossReason] = useState('');

  const markLostMutation = useMutation({
    mutationFn: async () => {
      if (!workspaceId) throw new Error('No workspace available');
      
      const { DealsService } = await import('@/data/deals.service');
      
      const updatePayload: any = {
        id: deal.id,
        workspace_id: workspaceId,
        deal_status: 'lost',
        stage: 'lost',
        loss_reason: lossReason || 'other',
        lost_at: new Date().toISOString(),
        closed_at: new Date().toISOString(),
      };
      
      const updatedDeal = await DealsService.updateDeal(updatePayload);
      const leadId = updatedDeal.lead_id || deal.lead_id;

      // Update lead pipeline_stage to 'lost' if lead exists
      if (leadId) {
        try {
          await supabase
            .from('leads')
            .update({ pipeline_stage: 'lost' } as any)
            .eq('id', leadId);
        } catch (error) {
          console.warn('Failed to update lead pipeline_stage', error);
        }
      }

      // Log the action event
      if (user?.id && leadId) {
        try {
          await supabase.from('action_events').insert({
            workspace_id: workspaceId,
            user_id: user.id,
            lead_id: leadId,
            action_type: 'stage_change',
            outcome: 'closed_lost',
            meta: { 
              deal_id: deal.id, 
              deal_status: 'lost',
              loss_reason: lossReason,
            },
          } as any);
        } catch (error) {
          console.warn('Failed to log action event', error);
        }
      }

      return updatedDeal;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['leads', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['revenue-analytics', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats', workspaceId] });
      
      const reasonLabel = LOSS_REASONS.find(r => r.value === lossReason)?.label || lossReason;
      toast.success('Deal Marked as Lost', {
        description: `${deal.lead?.name || 'Deal'} — ${reasonLabel}`
      });
      
      setLossReason('');
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast.error('Error', { description: errorMessage(error, 'Failed to mark deal as lost') });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!lossReason) {
      toast.error('Please select a reason');
      return;
    }
    markLostMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <XCircle className="w-5 h-5 text-destructive" />
            Why?
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="font-medium text-sm">{deal.lead?.name || 'Deal'}</p>
            {deal.value && (
              <p className="text-xs text-muted-foreground mt-1">
                {deal.value.toLocaleString()} {deal.currency}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              {LOSS_REASONS.map(reason => (
                <Button
                  key={reason.value}
                  type="button"
                  variant={lossReason === reason.value ? 'destructive' : 'outline'}
                  onClick={() => setLossReason(reason.value)}
                  className="h-10 text-sm"
                >
                  {reason.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} size="sm">
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="destructive"
              disabled={!lossReason || markLostMutation.isPending}
              size="sm"
            >
              {markLostMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <XCircle className="w-4 h-4 mr-2" />
              Mark Lost
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

